/**
 * OTP Verification Modal Component
 * Modal de vérification du code OTP pour l'authentification
 */
import React, { useState, useEffect, useRef } from 'react';
import { X, Mail, Shield, RefreshCw, Smartphone, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import config from 'config';

const API_URL = config.API_URL;

export default function OTPVerificationModal({ 
  isOpen, 
  onClose, 
  email,        // Email complet pour l'API
  maskedEmail,  // Email masqué pour l'affichage
  onSuccess,
  onResend 
}) {
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [rememberDevice, setRememberDevice] = useState(true);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [resending, setResending] = useState(false);
  const inputRefs = useRef([]);

  // Focus first input on open
  useEffect(() => {
    if (isOpen && inputRefs.current[0]) {
      setTimeout(() => inputRefs.current[0]?.focus(), 100);
    }
  }, [isOpen]);

  // Resend cooldown timer
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(c => c - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  const handleInputChange = (index, value) => {
    // Only allow digits
    if (value && !/^\d$/.test(value)) return;

    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);
    setError('');

    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    // Auto-submit when all digits entered
    if (value && index === 5) {
      const fullCode = newCode.join('');
      if (fullCode.length === 6) {
        handleVerify(fullCode);
      }
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pastedData.length === 6) {
      const newCode = pastedData.split('');
      setCode(newCode);
      handleVerify(pastedData);
    }
  };

  const handleVerify = async (codeToVerify = code.join('')) => {
    if (codeToVerify.length !== 6) {
      setError('Veuillez entrer le code à 6 chiffres');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_URL}/api/auth/otp/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: email,
          code: codeToVerify,
          remember_device: rememberDevice
        })
      });

      let data;
      try {
        data = await response.json();
      } catch (jsonErr) {
        setError('Erreur de réponse du serveur');
        return;
      }

      if (response.ok && data.status === 'authenticated') {
        // Save device token if provided
        if (data.device_token) {
          localStorage.setItem('ygo_device_token', data.device_token);
        }
        onSuccess(data);
      } else {
        // Afficher le message d'erreur du serveur
        setError(data.detail || data.message || 'Code invalide');
        setCode(['', '', '', '', '', '']);
        inputRefs.current[0]?.focus();
      }
    } catch (err) {
      console.error('OTP verification error:', err);
      setError('Erreur de connexion au serveur. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (resendCooldown > 0) return;

    setResending(true);
    try {
      const response = await fetch(`${API_URL}/api/auth/otp/resend`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });

      if (response.ok) {
        setResendCooldown(60);
        setCode(['', '', '', '', '', '']);
        setError('');
        inputRefs.current[0]?.focus();
      }
    } catch (err) {
      setError('Impossible de renvoyer le code');
    } finally {
      setResending(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Overlay */}
      <div 
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative w-full max-w-md bg-[#1F1F1F] border border-[#C9A24D]/20 shadow-2xl animate-in zoom-in-95 duration-200">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-[#E5E5E5]/40 hover:text-[#E5E5E5] transition-colors"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Content */}
        <div className="p-8">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-[#C9A24D]/10 mx-auto flex items-center justify-center mb-4">
              <Shield className="w-8 h-8 text-[#C9A24D]" />
            </div>
            <h2 className="font-['Playfair_Display'] text-2xl font-bold text-[#E5E5E5] mb-2">
              Vérification requise
            </h2>
            <p className="text-[#E5E5E5]/60 text-sm">
              Entrez le code à 6 chiffres envoyé à
            </p>
            <p className="text-[#C9A24D] text-sm font-medium mt-1 flex items-center justify-center gap-2">
              <Mail className="w-4 h-4" />
              {maskedEmail || email}
            </p>
          </div>

          {/* OTP Input */}
          <div className="flex justify-center gap-2 mb-6" onPaste={handlePaste}>
            {code.map((digit, index) => (
              <input
                key={index}
                ref={el => inputRefs.current[index] = el}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={digit}
                onChange={(e) => handleInputChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                className={`w-12 h-14 text-center text-2xl font-bold bg-[#0B0B0B] border-2 
                  ${error ? 'border-red-500' : digit ? 'border-[#C9A24D]' : 'border-[#C9A24D]/30'}
                  text-[#E5E5E5] focus:border-[#C9A24D] focus:outline-none transition-colors`}
                disabled={loading}
              />
            ))}
          </div>

          {/* Error message */}
          {error && (
            <div className="text-red-400 text-sm text-center mb-4 bg-red-500/10 p-2 border border-red-500/20">
              {error}
            </div>
          )}

          {/* Remember device */}
          <label className="flex items-center gap-3 mb-6 cursor-pointer group">
            <div className="relative">
              <input
                type="checkbox"
                checked={rememberDevice}
                onChange={(e) => setRememberDevice(e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-5 h-5 border-2 border-[#C9A24D]/40 bg-[#0B0B0B] peer-checked:bg-[#C9A24D] peer-checked:border-[#C9A24D] transition-all flex items-center justify-center">
                {rememberDevice && (
                  <svg className="w-3 h-3 text-[#0B0B0B]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </div>
            </div>
            <div className="flex-1">
              <span className="text-[#E5E5E5] text-sm group-hover:text-[#C9A24D] transition-colors flex items-center gap-2">
                <Smartphone className="w-4 h-4" />
                Se rappeler de cet appareil
              </span>
              <p className="text-[#E5E5E5]/40 text-xs mt-0.5">
                Ne plus demander de code pendant 30 jours
              </p>
            </div>
          </label>

          {/* Verify button */}
          <Button
            onClick={() => handleVerify()}
            disabled={loading || code.join('').length !== 6}
            className="w-full bg-[#C9A24D] hover:bg-[#E6C77A] text-[#0B0B0B] font-semibold py-3"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Vérification...
              </>
            ) : (
              'Vérifier et se connecter'
            )}
          </Button>

          {/* Resend */}
          <div className="mt-6 text-center">
            <p className="text-[#E5E5E5]/50 text-sm mb-2">
              Vous n'avez pas reçu le code ?
            </p>
            <button
              onClick={handleResend}
              disabled={resendCooldown > 0 || resending}
              className={`text-sm flex items-center gap-2 mx-auto transition-colors
                ${resendCooldown > 0 ? 'text-[#E5E5E5]/30 cursor-not-allowed' : 'text-[#C9A24D] hover:text-[#E6C77A]'}`}
            >
              <RefreshCw className={`w-4 h-4 ${resending ? 'animate-spin' : ''}`} />
              {resendCooldown > 0 
                ? `Renvoyer dans ${resendCooldown}s` 
                : 'Renvoyer le code'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
